import { useParams } from 'react-router-dom';

/**
 * Returns the current language from the URL /:lang param.
 * Falls back to 'es' if not present (during redirects).
 */
export function useCurrentLang(): string {
  const { lang } = useParams<{ lang: string }>();
  return lang === 'en' || lang === 'es' ? lang : 'es';
}

/**
 * Prepends the current language prefix to a path.
 * e.g. localizedPath('/pricing') => '/es/pricing'
 * Skips if path already starts with /en/ or /es/
 * Skips external URLs and hash links
 */
export function useLocalizedPath() {
  const lang = useCurrentLang();

  return (path: string): string => {
    // Don't localize external URLs, hash links, or paths that already have lang prefix
    if (!path || path.startsWith('http') || path.startsWith('#') || path.startsWith('mailto:')) {
      return path;
    }
    if (/^\/(en|es)(\/|$)/.test(path)) {
      return path;
    }
    // Don't localize admin, seller, support routes
    if (/^\/(admin|seller|support|checkout|dashboard|account|auth|chat)/.test(path)) {
      return path;
    }
    // Prepend language
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `/${lang}${cleanPath}`;
  };
}

/**
 * Switches language in a URL path.
 * e.g. switchLangInPath('/en/pricing', 'es') => '/es/pricing'
 */
export function switchLangInPath(currentPath: string, newLang: string): string {
  const match = currentPath.match(/^\/(en|es)(\/.*)?$/);
  if (match) {
    return `/${newLang}${match[2] || ''}`;
  }
  // If no lang prefix, add one
  return `/${newLang}${currentPath}`;
}
